import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Ticket, ArrowLeft, RefreshCw, Plus, Copy, Trash2, CheckCircle,
  DollarSign, Users, Tag, Gift, Search, Download
} from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

const PassAdmin = () => {
  const [loading, setLoading] = useState(true);
  const [coupons, setCoupons] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [activeTab, setActiveTab] = useState('coupons');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newCoupon, setNewCoupon] = useState({
    tier_name: 'SILVER',
    quantity: 1,
    max_uses: 1,
    code: ''
  });
  const [bulkCreate, setBulkCreate] = useState({
    tier_name: 'SILVER',
    quantity: 1,
    count: 10,
    max_uses: 1
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [couponsRes, tiersRes] = await Promise.all([
        api.get('/admin/p2p/coupons'),
        api.get('/admin/p2p/tiers')
      ]);
      setCoupons(couponsRes.data || []);
      setTiers(tiersRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const createCoupon = async () => {
    try {
      const response = await api.post('/admin/p2p/coupon', newCoupon);
      toast.success(`Coupon created: ${response.data.coupon?.code}`);
      setShowCreateModal(false);
      setNewCoupon({ tier_name: 'SILVER', quantity: 1, max_uses: 1, code: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create coupon');
    }
  };

  const createBulkCoupons = async () => {
    try {
      const response = await api.post('/admin/p2p/coupons/bulk', bulkCreate);
      toast.success(`Created ${response.data.codes?.length || 0} coupons`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create coupons');
    }
  };

  const deleteCoupon = async (couponId) => {
    if (!window.confirm('Deactivate this coupon?')) return;
    try {
      await api.delete(`/admin/p2p/coupon/${couponId}`);
      toast.success('Coupon deactivated');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete coupon');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Code copied!');
  };

  const downloadCoupons = () => {
    const csv = 'Code,Tier,Quantity,Max Uses,Status,Redemptions\n' + 
      coupons.map(c => `${c.code},${c.tier_name},${c.quantity},${c.max_uses || 'Unlimited'},${c.is_active ? 'Active' : 'Inactive'},${c.redemption_count || 0}`).join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'coupons.csv';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Downloaded!');
  };

  const activeCoupons = coupons.filter(c => c.is_active);
  const usedCoupons = coupons.filter(c => !c.is_active);

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="pass-admin">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <Link to="/admin" className="text-muted-foreground hover:text-white text-sm flex items-center gap-1 mb-2">
              <ArrowLeft className="w-4 h-4" /> Back to Admin
            </Link>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Ticket className="w-8 h-8 text-purple-500" />
              Pass & Coupon Management
            </h1>
          </div>
          <button onClick={fetchData} className="p-3 rounded-xl bg-white/10 hover:bg-white/20">
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="glass-card p-5">
            <Gift className="w-7 h-7 text-purple-500 mb-2" />
            <p className="text-2xl font-bold">{coupons.length}</p>
            <p className="text-sm text-muted-foreground">Total Coupons</p>
          </div>
          <div className="glass-card p-5">
            <CheckCircle className="w-7 h-7 text-green-500 mb-2" />
            <p className="text-2xl font-bold">{activeCoupons.length}</p>
            <p className="text-sm text-muted-foreground">Active Coupons</p>
          </div>
          <div className="glass-card p-5">
            <Users className="w-7 h-7 text-blue-500 mb-2" />
            <p className="text-2xl font-bold">{coupons.reduce((acc, c) => acc + (c.redemption_count || 0), 0)}</p>
            <p className="text-sm text-muted-foreground">Total Redemptions</p>
          </div>
          <div className="glass-card p-5">
            <Tag className="w-7 h-7 text-yellow-500 mb-2" />
            <p className="text-2xl font-bold">{tiers.length}</p>
            <p className="text-sm text-muted-foreground">Pass Tiers</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {['coupons', 'create', 'bulk'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg font-medium ${
                activeTab === tab ? 'bg-primary text-white' : 'bg-white/10 hover:bg-white/20'
              }`}
            >
              {tab === 'create' ? 'Create Coupon' : tab === 'bulk' ? 'Bulk Create' : 'All Coupons'}
            </button>
          ))}
        </div>

        {/* Coupons List */}
        {activeTab === 'coupons' && (
          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">All Coupons</h2>
              <button onClick={downloadCoupons} className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20">
                <Download className="w-4 h-4" /> Export CSV
              </button>
            </div>

            {coupons.length > 0 ? (
              <div className="space-y-3">
                {coupons.map((coupon) => (
                  <div 
                    key={coupon.id} 
                    className={`p-4 rounded-xl flex items-center justify-between ${
                      coupon.is_active ? 'bg-green-500/10 border border-green-500/20' : 'bg-white/5 opacity-60'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-purple-500/20 rounded-xl">
                        <Ticket className="w-6 h-6 text-purple-500" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <code className="text-lg font-bold font-mono">{coupon.code}</code>
                          <button onClick={() => copyCode(coupon.code)} className="p-1 hover:bg-white/10 rounded">
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {coupon.tier_name} • {coupon.quantity} pass(es) • 
                          Uses: {coupon.redemption_count || 0}/{coupon.max_uses || '∞'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        coupon.is_active ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'
                      }`}>
                        {coupon.is_active ? 'Active' : 'Inactive'}
                      </span>
                      {coupon.is_active && (
                        <button
                          onClick={() => deleteCoupon(coupon.id)}
                          className="p-2 rounded-lg bg-red-500/20 text-red-500 hover:bg-red-500/30"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Gift className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">No coupons created yet</p>
              </div>
            )}
          </div>
        )}

        {/* Create Single Coupon */}
        {activeTab === 'create' && (
          <div className="glass-card p-6 max-w-lg">
            <h2 className="text-xl font-bold mb-4">Create Single Coupon</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm mb-2">Pass Tier</label>
                <select
                  value={newCoupon.tier_name}
                  onChange={(e) => setNewCoupon({ ...newCoupon, tier_name: e.target.value })}
                  className="w-full px-4 py-3 bg-white/10 border border-white/10 rounded-xl"
                >
                  {tiers.map((tier) => (
                    <option key={tier.id} value={tier.name}>{tier.name} (₹{tier.price})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm mb-2">Quantity (passes per coupon)</label>
                <input
                  type="number"
                  value={newCoupon.quantity}
                  onChange={(e) => setNewCoupon({ ...newCoupon, quantity: parseInt(e.target.value) })}
                  min="1"
                  className="w-full px-4 py-3 bg-white/10 border border-white/10 rounded-xl"
                />
              </div>
              <div>
                <label className="block text-sm mb-2">Max Uses (empty = unlimited)</label>
                <input
                  type="number"
                  value={newCoupon.max_uses || ''}
                  onChange={(e) => setNewCoupon({ ...newCoupon, max_uses: e.target.value ? parseInt(e.target.value) : null })}
                  min="1"
                  placeholder="Unlimited"
                  className="w-full px-4 py-3 bg-white/10 border border-white/10 rounded-xl"
                />
              </div>
              <div>
                <label className="block text-sm mb-2">Custom Code (optional)</label>
                <input
                  type="text"
                  value={newCoupon.code}
                  onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value.toUpperCase() })}
                  placeholder="Auto-generate if empty"
                  className="w-full px-4 py-3 bg-white/10 border border-white/10 rounded-xl font-mono"
                />
              </div>
              <button
                onClick={createCoupon}
                className="w-full py-3 bg-primary text-white rounded-xl font-semibold hover:bg-primary/90"
              >
                Create Coupon
              </button>
            </div>
          </div>
        )}

        {/* Bulk Create */}
        {activeTab === 'bulk' && (
          <div className="glass-card p-6 max-w-lg">
            <h2 className="text-xl font-bold mb-4">Bulk Create Coupons</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm mb-2">Pass Tier</label>
                <select
                  value={bulkCreate.tier_name}
                  onChange={(e) => setBulkCreate({ ...bulkCreate, tier_name: e.target.value })}
                  className="w-full px-4 py-3 bg-white/10 border border-white/10 rounded-xl"
                >
                  {tiers.map((tier) => (
                    <option key={tier.id} value={tier.name}>{tier.name} (₹{tier.price})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm mb-2">Number of Coupons to Create</label>
                <input
                  type="number"
                  value={bulkCreate.count}
                  onChange={(e) => setBulkCreate({ ...bulkCreate, count: parseInt(e.target.value) })}
                  min="1"
                  max="100"
                  className="w-full px-4 py-3 bg-white/10 border border-white/10 rounded-xl"
                />
                <p className="text-xs text-muted-foreground mt-1">Max 100 at a time</p>
              </div>
              <div>
                <label className="block text-sm mb-2">Passes per Coupon</label>
                <input
                  type="number"
                  value={bulkCreate.quantity}
                  onChange={(e) => setBulkCreate({ ...bulkCreate, quantity: parseInt(e.target.value) })}
                  min="1"
                  className="w-full px-4 py-3 bg-white/10 border border-white/10 rounded-xl"
                />
              </div>
              <div>
                <label className="block text-sm mb-2">Max Uses per Coupon</label>
                <input
                  type="number"
                  value={bulkCreate.max_uses}
                  onChange={(e) => setBulkCreate({ ...bulkCreate, max_uses: parseInt(e.target.value) })}
                  min="1"
                  className="w-full px-4 py-3 bg-white/10 border border-white/10 rounded-xl"
                />
              </div>
              <button
                onClick={createBulkCoupons}
                className="w-full py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-semibold hover:opacity-90"
              >
                Generate {bulkCreate.count} Coupons
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PassAdmin;
